import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/AuthContext';
import { useVenue } from '../components/shared/VenueContext';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Bot, Send, Loader2, User, Sparkles,
  Thermometer, SprayCan, AlertTriangle, FileText,
  ShieldCheck, ArrowRight, Info, ChevronDown, ChevronUp,
  Copy, Check, Scale, AlertCircle, Clock, BookOpen,
  ThumbsUp, ThumbsDown, ExternalLink
} from 'lucide-react';

const QUICK_PROMPTS = [
  { icon: Thermometer, text: "He registrat 9°C a la nevera, és un problema?" },
  { icon: AlertTriangle, text: "Estic preparat per una inspecció sanitària?" },
  { icon: SprayCan, text: "Puc fer servir aquest desinfectant a superfícies de contacte alimentari?" },
  { icon: ShieldCheck, text: "Quins registres APPCC he de portar al dia?" },
  { icon: Info, text: "Quin és el protocol de descongelació correcte?" },
  { icon: Scale, text: "És obligatori informar dels al·lèrgens?" },
];

const CONFIDENCE_CONFIG = {
  high: { color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", dot: "bg-emerald-500", label: "Alta" },
  medium: { color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", dot: "bg-amber-500", label: "Mitjana" },
  low: { color: "text-red-600", bg: "bg-red-50", border: "border-red-200", dot: "bg-red-500", label: "Baixa" },
};

const URGENCY_CONFIG = {
  critical: { color: "text-red-700", bg: "bg-red-50", border: "border-red-300", icon: AlertCircle, label: "Acció immediata" },
  high: { color: "text-orange-700", bg: "bg-orange-50", border: "border-orange-200", icon: AlertTriangle, label: "Prioritari" },
  medium: { color: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200", icon: Clock, label: "Revisió recomanada" },
  low: { color: "text-gray-600", bg: "bg-gray-50", border: "border-gray-200", icon: Info, label: "Informatiu" },
};

const ACTION_URGENCY_COLORS = {
  critical: "bg-red-50 border-red-200 text-red-700 hover:bg-red-100",
  high: "bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100",
  medium: "bg-white border-gray-200 text-gray-700 hover:bg-gray-50",
  low: "bg-white border-gray-200 text-gray-500 hover:bg-gray-50",
};

function ConfidenceBadge({ confidence }) {
  if (!confidence) return null;
  const cfg = CONFIDENCE_CONFIG[confidence.level] || CONFIDENCE_CONFIG.medium;
  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${cfg.bg} ${cfg.color} ${cfg.border} border`} data-testid="confidence-badge">
      <div className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      Confiança: {cfg.label}
      {confidence.score !== undefined && <span className="opacity-60 ml-0.5">({confidence.score}/100)</span>}
    </div>
  );
}

function UrgencyBanner({ urgency }) {
  if (!urgency || urgency.level === "low") return null;
  const cfg = URGENCY_CONFIG[urgency.level] || URGENCY_CONFIG.low;
  const Icon = cfg.icon;
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium ${cfg.bg} ${cfg.color} ${cfg.border} border mb-2`} data-testid="urgency-banner">
      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
      <span>{cfg.label}: {urgency.reason}</span>
    </div>
  );
}

function IntentBadge({ intent }) {
  if (!intent) return null;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#7B2D3F]/10 text-[#7B2D3F] border border-[#7B2D3F]/20" data-testid="intent-badge">
      {intent.label || intent.primary}
    </span>
  );
}

function ActionButtons({ actions, navigate }) {
  if (!actions || actions.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {actions.map((a, i) => {
        const colors = ACTION_URGENCY_COLORS[a.urgency] || ACTION_URGENCY_COLORS.medium;
        return (
          <button
            key={`alert-${i}`}
            onClick={() => navigate(`/${a.module}`)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${colors}`}
            data-testid={`action-btn-${a.action}`}
          >
            <ArrowRight className="w-3 h-3" />
            {a.label}
            {a.urgency === "critical" && <AlertCircle className="w-3 h-3 ml-0.5" />}
          </button>
        );
      })}
    </div>
  );
}

function SafeguardNotices({ safeguards }) {
  if (!safeguards || safeguards.safeguards_triggered === 0) return null;
  return (
    <div className="mt-2 space-y-1" data-testid="safeguard-notices">
      {safeguards.disclaimers.map((d, i) => (
        <div key={`day-${i}`} className="flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50/80 rounded-lg px-3 py-2 border border-amber-200">
          <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-amber-600" />
          <span>{d}</span>
        </div>
      ))}
    </div>
  );
}

function SourcesPanel({ sources, expanded, onToggle }) {
  if (!sources || sources.length === 0) return null;
  return (
    <div className="border-t border-gray-100" data-testid="sources-panel">
      <button
        onClick={onToggle}
        className="flex items-center gap-1.5 px-3 py-2 text-[11px] text-gray-500 hover:text-[#7B2D3F] w-full transition-colors"
      >
        <BookOpen className="w-3 h-3" />
        <span className="font-medium">{sources.length} font{sources.length > 1 ? 's' : ''} legal{sources.length > 1 ? 's' : ''}</span>
        {expanded ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
      </button>
      {expanded && (
        <div className="px-3 pb-2.5 space-y-1.5">
          {sources.map((s, i) => (
            <div key={`step-${i}`} className="flex items-start gap-2 text-[11px] bg-gray-50/80 rounded-md px-2.5 py-1.5">
              <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${s.level === 'obligatori' ? 'bg-red-400' : 'bg-blue-400'}`} />
              <div>
                <span className="font-medium text-gray-700">{s.name}</span>
                {s.articles && <span className="text-gray-400 ml-1">({s.articles})</span>}
                <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-semibold ${s.level === 'obligatori' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>
                  {s.level === 'obligatori' ? 'OBLIGATORI' : 'RECOMANACIÓ'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FollowUpChips({ followUps, onSelect, disabled }) {
  if (!followUps || followUps.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2" data-testid="follow-up-chips">
      {followUps.map((q, i) => (
        <button
          key={`q-${i}`}
          onClick={() => onSelect(q)}
          disabled={disabled}
          className="text-[11px] px-3 py-1.5 rounded-full bg-[#7B2D3F]/5 text-[#7B2D3F] border border-[#7B2D3F]/15 hover:bg-[#7B2D3F]/10 transition-all disabled:opacity-50"
          data-testid={`follow-up-${i}`}
        >
          {q}
        </button>
      ))}
    </div>
  );
}

// Parses markdown-like tags (**bold**, [LINK:module:label], [OBLIGATORI], etc.)
// and returns an array of React nodes — NO dangerouslySetInnerHTML, no XSS.
const TAG_BADGE_STYLES = {
  OBLIGATORI: "bg-red-100 text-red-700",
  RECOMANABLE: "bg-blue-100 text-blue-700",
  "ACCIÓ HO-SYS": "bg-purple-100 text-purple-700",
  "ACCIO HO-SYS": "bg-purple-100 text-purple-700",
};

function renderInlineMarkup(text, lineKey) {
  // Regex matches: **bold**, [LINK:module:label], [OBLIGATORI], [RECOMANABLE], [ACCIÓ HO-SYS]
  const pattern = /(\*\*([^*]+?)\*\*)|(\[LINK:([^:\]]+):([^\]]+)\])|(\[(OBLIGATORI|RECOMANABLE|ACCIÓ HO-SYS|ACCIO HO-SYS)\])/g;
  const out = [];
  let lastIndex = 0;
  let match;
  let nodeIdx = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      out.push(text.slice(lastIndex, match.index));
    }
    if (match[2] !== undefined) {
      // **bold**
      out.push(<b key={`${lineKey}-b-${nodeIdx}`}>{match[2]}</b>);
    } else if (match[4] !== undefined) {
      // [LINK:module:label]
      out.push(
        <a
          key={`${lineKey}-a-${nodeIdx}`}
          className="text-[#7B2D3F] underline cursor-pointer font-medium"
          data-module={match[4]}
        >
          {match[5]}
        </a>
      );
    } else if (match[7] !== undefined) {
      // [OBLIGATORI] / [RECOMANABLE] / [ACCIÓ HO-SYS]
      const tag = match[7];
      out.push(
        <span
          key={`${lineKey}-tag-${nodeIdx}`}
          className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold align-middle ${TAG_BADGE_STYLES[tag] || "bg-gray-100 text-gray-700"}`}
        >
          {tag}
        </span>
      );
    }
    lastIndex = pattern.lastIndex;
    nodeIdx++;
  }
  if (lastIndex < text.length) {
    out.push(text.slice(lastIndex));
  }
  return out;
}

function MessageContent({ content }) {
  const lines = content.split("\n");
  return (
    <div className="text-sm whitespace-pre-wrap leading-relaxed">
      {lines.map((line, i) => (
        <p
          key={`msg-line-${i}-${line.length}`}
          className={line.trim() === "" ? "h-2" : ""}
        >
          {renderInlineMarkup(line, `${i}`)}
        </p>
      ))}
    </div>
  );
}

function FeedbackButtons({ msgIdx, feedback, onFeedback }) {
  const current = feedback[msgIdx];
  return (
    <div className="flex items-center gap-1 mt-1.5">
      <button
        onClick={() => onFeedback(msgIdx, 'positive')}
        className={`p-1 rounded-md transition-all ${current === 'positive' ? 'bg-emerald-100 text-emerald-600' : 'text-gray-300 hover:text-gray-500 hover:bg-gray-50'}`}
        data-testid={`feedback-positive-${msgIdx}`}
      >
        <ThumbsUp className="w-3 h-3" />
      </button>
      <button
        onClick={() => onFeedback(msgIdx, 'negative')}
        className={`p-1 rounded-md transition-all ${current === 'negative' ? 'bg-red-100 text-red-600' : 'text-gray-300 hover:text-gray-500 hover:bg-gray-50'}`}
        data-testid={`feedback-negative-${msgIdx}`}
      >
        <ThumbsDown className="w-3 h-3" />
      </button>
    </div>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [text]);
  return (
    <button onClick={handleCopy} className="p-1 rounded-md text-gray-300 hover:text-gray-500 hover:bg-gray-50 transition-all" data-testid="copy-response">
      {copied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
    </button>
  );
}

export default function AIAssistant() {
  const { user } = useAuth();
  const { currentVenue } = useVenue();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedSources, setExpandedSources] = useState({});
  const [expandedMeta, setExpandedMeta] = useState({});
  const [feedback, setFeedback] = useState({});
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
    // Prevent outer page scroll when messages change
    window.scrollTo(0, 0);
  }, [messages]);

  const handleSend = async (text = input) => {
    if (!text.trim() || loading) return;

    const userMessage = { role: 'user', content: text, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const context = currentVenue
        ? `Restaurant: ${currentVenue.name}, Ciutat: ${currentVenue.city || 'No especificada'}`
        : '';

      const response = await api.post('/api/ai/chat', {
        message: text,
        context: context,
        history: messages.slice(-10).map(m => ({ role: m.role, content: m.content })),
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.response,
        timestamp: new Date(),
        intent: response.intent,
        confidence: response.confidence,
        urgency: response.urgency,
        actions: response.actions,
        safeguards: response.safeguards,
        follow_ups: response.follow_ups,
        sources: response.sources,
        template: response.template,
        is_audit: response.is_audit,
        cross_alerts: response.cross_alerts,
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      console.error('Error:', err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Ho sento, hi ha hagut un error processant la teva consulta. Torna-ho a provar.',
        timestamp: new Date(),
        error: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleFeedback = useCallback(async (msgIdx, type) => {
    setFeedback(prev => ({ ...prev, [msgIdx]: prev[msgIdx] === type ? null : type }));
    try {
      await api.post('/api/ai/feedback', { message_index: msgIdx, feedback: type });
    } catch (e) { /* non-critical */ }
  }, []);

  const toggleSources = (idx) => setExpandedSources(prev => ({ ...prev, [idx]: !prev[idx] }));
  const toggleMeta = (idx) => setExpandedMeta(prev => ({ ...prev, [idx]: !prev[idx] }));

  return (
    <div className="flex flex-col" data-testid="ai-assistant-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#7B2D3F] to-[#7B2D3F]/70 flex items-center justify-center shadow-lg">
          <Bot className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Assistent IA de Compliance</h1>
          <p className="text-gray-500 text-sm">Motor de compliance amb fonts legals UE/ES/CAT verificades</p>
        </div>
      </div>

      <Card className="border-0 shadow-sm overflow-hidden flex flex-col">
        <div ref={messagesContainerRef} className="overflow-y-auto p-4 space-y-4" style={{ maxHeight: 'calc(100vh - 300px)', minHeight: '200px' }}>
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-6">
              <div className="w-16 h-16 rounded-3xl bg-[#7B2D3F]/10 flex items-center justify-center mb-4">
                <Sparkles className="w-8 h-8 text-[#7B2D3F]" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Hola, {user?.name?.split(' ')[0]}!</h2>
              <p className="text-gray-500 text-sm mb-6 max-w-lg">
                Sóc l'assistent de compliance de Ho-Sys. Les meves respostes inclouen fonts legals verificades,
                nivell de confiança, i accions directes dins la plataforma.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 w-full max-w-2xl">
                {QUICK_PROMPTS.map((prompt, idx) => (
                  <button
                    key={`prompt-${idx}`}
                    onClick={() => handleSend(prompt.text)}
                    data-testid={`quick-prompt-${idx}`}
                    className="flex items-center gap-2.5 p-3 rounded-xl bg-gray-50 hover:bg-[#7B2D3F]/5 hover:border-[#7B2D3F]/20 border border-transparent text-left text-sm text-gray-700 transition-all"
                  >
                    <prompt.icon className="w-4 h-4 text-[#7B2D3F] flex-shrink-0" />
                    <span className="line-clamp-2">{prompt.text}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg, idx) => (
                <div key={`msg-${idx}`} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`} data-testid={`message-${idx}`}>
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    msg.role === 'user' ? 'bg-[#7B2D3F]' : 'bg-gray-100'
                  }`}>
                    {msg.role === 'user' ? (
                      <User className="w-4 h-4 text-white" />
                    ) : (
                      <Bot className="w-4 h-4 text-gray-600" />
                    )}
                  </div>
                  <div className={`max-w-[85%] ${msg.role === 'user' ? 'text-right' : ''}`}>
                    {msg.role === 'user' ? (
                      <div className="inline-block p-3 rounded-2xl bg-[#7B2D3F] text-white rounded-tr-md">
                        <p className="text-sm">{msg.content}</p>
                      </div>
                    ) : (
                      <div className={`rounded-2xl rounded-tl-md overflow-hidden ${msg.error ? 'bg-red-50 border border-red-200' : 'bg-white border border-gray-200 shadow-sm'}`}>
                        {/* Urgency banner (critical/high only) */}
                        {msg.urgency && msg.urgency.level !== "low" && (
                          <div className="px-3 pt-2.5">
                            <UrgencyBanner urgency={msg.urgency} />
                          </div>
                        )}

                        {/* Cross-alerts (compound risks) */}
                        {msg.cross_alerts && msg.cross_alerts.length > 0 && (
                          <div className="px-3 pt-1 space-y-1" data-testid="cross-alerts">
                            {msg.cross_alerts.map((ca, ci) => (
                              <div key={`el-${ci}`} className={`flex items-start gap-2 px-3 py-2 rounded-lg text-[11px] font-medium border ${
                                ca.severity === 'critical' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-orange-50 text-orange-700 border-orange-200'
                              }`}>
                                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                <span>{ca.message}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Intent + Confidence + Template badges */}
                        {(msg.intent || msg.confidence) && (
                          <div className="flex items-center flex-wrap gap-2 px-3 pt-2.5 pb-1">
                            {msg.is_audit && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-700 border border-purple-200" data-testid="audit-badge">
                                MODE AUDITORIA
                              </span>
                            )}
                            <IntentBadge intent={msg.intent} />
                            <ConfidenceBadge confidence={msg.confidence} />
                            {msg.template && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-gray-100 text-gray-500">
                                T{msg.template}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Message content */}
                        <div className="px-3 py-2">
                          <MessageContent content={msg.content} />
                        </div>

                        {/* Safeguard notices */}
                        {msg.safeguards && msg.safeguards.safeguards_triggered > 0 && (
                          <div className="px-3 pb-1">
                            <SafeguardNotices safeguards={msg.safeguards} />
                          </div>
                        )}

                        {/* Action buttons */}
                        {msg.actions && msg.actions.length > 0 && (
                          <div className="px-3 pb-2">
                            <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wide mb-1">Accions a Ho-Sys</p>
                            <ActionButtons actions={msg.actions} navigate={navigate} />
                          </div>
                        )}

                        {/* Follow-up suggestions */}
                        {msg.follow_ups && msg.follow_ups.length > 0 && (
                          <div className="px-3 pb-2.5">
                            <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wide mb-1">Preguntes relacionades</p>
                            <FollowUpChips followUps={msg.follow_ups} onSelect={handleSend} disabled={loading} />
                          </div>
                        )}

                        {/* Sources panel */}
                        <SourcesPanel sources={msg.sources} expanded={expandedSources[idx]} onToggle={() => toggleSources(idx)} />

                        {/* Confidence detail (expandable) */}
                        {msg.confidence?.factors && (
                          <div className="border-t border-gray-100">
                            <button
                              onClick={() => toggleMeta(idx)}
                              className="flex items-center gap-1 px-3 py-1.5 text-[10px] text-gray-400 hover:text-gray-600 w-full"
                            >
                              {expandedMeta[idx] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                              Detalls de confiança
                            </button>
                            {expandedMeta[idx] && (
                              <div className="px-3 pb-2 space-y-0.5">
                                {msg.confidence.factors.map((f, fi) => (
                                  <p key={`el-${fi}`} className="text-[11px] text-gray-500">· {f}</p>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Footer: feedback + copy */}
                        <div className="flex items-center justify-between px-3 py-1.5 border-t border-gray-100">
                          <FeedbackButtons msgIdx={idx} feedback={feedback} onFeedback={handleFeedback} />
                          <CopyButton text={msg.content} />
                        </div>
                      </div>
                    )}
                    <p className="text-[10px] text-gray-400 mt-1 px-1">
                      {msg.timestamp.toLocaleTimeString('ca-ES', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-gray-600" />
                  </div>
                  <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-md p-3 shadow-sm">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-[#7B2D3F]" />
                      <span className="text-sm text-gray-500">Classificant consulta, cercant fonts legals i analitzant dades...</span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input */}
        <div className="p-4 border-t border-gray-100 bg-white">
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Pregunta sobre APPCC, al·lèrgens, temperatures, inspeccions..."
              disabled={loading}
              className="flex-1"
              data-testid="ai-input"
            />
            <Button
              onClick={() => handleSend()}
              disabled={!input.trim() || loading}
              className="px-4 bg-[#7B2D3F] hover:bg-[#7B2D3F]/90"
              data-testid="ai-send-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          <p className="text-[10px] text-gray-400 mt-2 text-center">
            Motor de compliance amb classificació d'intents, fonts legals UE/ES/CAT i scoring de confiança
          </p>
        </div>
      </Card>
    </div>
  );
}
